import React, { useEffect, useMemo } from 'react';

import Image from 'next/image';
import dynamic from 'next/dynamic';

import { BankInstallmentsViewModel } from '@api/index';
import { getCurrencySymbol } from '@utils/utils';
import { Col, FlexibleDiv, Row } from '@components/formStyles';
import CustomIcon from '@components/utils/CustomIcon';
import { CloseIcon } from '@assets/icons';
import { useDevice } from '@contexts/device';

import {
  Overlay,
  Wrapper,
  Header,
  Title,
  TableRow,
  InstallmentNumber,
  TableCol,
  Total,
  ResponsiveHeader,
  CloseAction,
  ResponsiveCloseAction,
  TableContainer,
} from './styled';
import { getInstallmentPrices } from './helpers';

const OriginalPrice = dynamic(
  () => import('@components/ui/price/OriginalPrice')
);

export interface BankInstallmentsProps {
  bankInstallments: BankInstallmentsViewModel[];
  onClose?: () => void;
}

const BankInstallments = ({
  bankInstallments,
  onClose,
}: BankInstallmentsProps) => {
  const { isDesktop } = useDevice();

  useEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      body.style.overflow = 'hidden';
      return () => {
        setTimeout(() => {
          body.style.overflow = 'initial';
        }, 0);
      };
    }
  }, []);
  const installmentControl = useMemo(() => {
    return bankInstallments?.find((item) =>
      item.installments.find(
        (i) =>
          (i.additionalInstallmentCount &&
            i.count + i.additionalInstallmentCount === 18) ||
          i.additionalInstallmentCount === 18
      )
    )
      ? true
      : false;
  }, [bankInstallments]);

  const installment_numbers = useMemo(() => {
    return installmentControl ? [1, 3, 6, 9, 12, 18] : [1, 3, 6, 9, 12];
  }, [installmentControl]);
  return (
    <Overlay>
      {!isDesktop ? (
        <ResponsiveHeader>
          <div />
          <Title>Taksit Seçenekleri</Title>
          <ResponsiveCloseAction data-testid='close-action' onClick={onClose}>
            <CustomIcon
              icon={CloseIcon}
              fontSize={24}
              width={24}
              height={24}
              paletteColor='black'
            />
          </ResponsiveCloseAction>
        </ResponsiveHeader>
      ) : null}
      <TableContainer installmentControl={installmentControl}>
        <Wrapper>
          {isDesktop ? (
            <Header>
              <Title>Taksit Seçenekleri</Title>
              <CloseAction onClick={onClose}>
                <FlexibleDiv top={2}>
                  <CustomIcon
                    icon={CloseIcon}
                    fontSize={24}
                    width={24}
                    height={24}
                    paletteColor='black'
                  />
                </FlexibleDiv>
              </CloseAction>
            </Header>
          ) : null}

          <TableRow>
            <InstallmentNumber />
            <InstallmentNumber>Peşin</InstallmentNumber>
            <InstallmentNumber>3 Taksit</InstallmentNumber>
            <InstallmentNumber>6 Taksit</InstallmentNumber>
            <InstallmentNumber>9 Taksit</InstallmentNumber>
            <InstallmentNumber>12 Taksit</InstallmentNumber>
            {installmentControl ? (
              <InstallmentNumber>18 Taksit</InstallmentNumber>
            ) : null}
          </TableRow>
          {bankInstallments?.map((bank) => (
            <TableRow key={bank.bankName}>
              <TableCol>
                <Image
                  alt={bank.bankName ?? ''}
                  src={bank.imageUrl || ''}
                  width={129}
                  height={59}
                />
              </TableCol>
              {installment_numbers.map((installmentNumber) => {
                const isMatch = bank?.installments?.find(
                  (installment) =>
                    installment.count +
                      (installment.additionalInstallmentCount || 0) ===
                    installmentNumber
                );
                return isMatch ? (
                  <TableCol>
                    <Col>
                      {installmentNumber !== 1 ? (
                        <>
                          <Row justifyContent='center'>
                            <OriginalPrice
                              noLine
                              currency={
                                getCurrencySymbol(bank.currencyCode) || '₺'
                              }
                              price={
                                getInstallmentPrices(
                                  bank?.installments ?? [],
                                  installmentNumber
                                )?.price
                              }
                            />

                            <FlexibleDiv left={3} right={3}>
                              x
                            </FlexibleDiv>
                            {installmentNumber}
                          </Row>
                          <Row justifyContent='center'>
                            <Total>Toplam:</Total>
                            <OriginalPrice
                              noLine
                              currency={
                                getCurrencySymbol(bank?.currencyCode) || '₺'
                              }
                              price={
                                getInstallmentPrices(
                                  bank?.installments ?? [],
                                  installmentNumber
                                )?.totalPrice
                              }
                            />
                          </Row>
                        </>
                      ) : (
                        <OriginalPrice
                          noLine
                          currency={getCurrencySymbol(bank.currencyCode) || '₺'}
                          price={
                            bank?.installments?.find(
                              (installment) => installment.count === 1
                            )?.totalPrice
                          }
                        />
                      )}
                    </Col>
                  </TableCol>
                ) : (
                  <TableCol>
                    <span>-</span>
                  </TableCol>
                );
              })}
            </TableRow>
          ))}
        </Wrapper>
      </TableContainer>
    </Overlay>
  );
};

export default BankInstallments;
